<template>
    <v-layout style="position: relative; min-height: 200px;" align-center justify-center>
        <no-data v-if="!cropProfile" message="No crop profile found"></no-data>
        <loading v-else-if="loading"></loading>
        <crop-profile-chart v-else :data="chartData" :options="chartOptions" ref="cropProfileChart"></crop-profile-chart>
    </v-layout>
</template>

<script>
import Loading from '@/components/generic/Loading.vue';
import NoData from '@/components/generic/NoData.vue';
import CropProfileChart from '@/components/crops/CropProfileChart.vue';
import ForecastingUtilService from '@/services/forecastingUtilService';
import moment from 'moment';
import UtilService from '@/services/utilService.js'
import Chart from 'chart.js'

export default {
    components: {
        Loading,
        NoData,
        CropProfileChart
    },
    props: [
        'financialYearCalendars',
        'cropProfile',        
        'displayValueTypeCode',        
        'loading',
        'cropForecasts'
    ],
    data(){
        return {            
        };
    },
    computed: {      
        startDate(){
            let date = ForecastingUtilService.getFinancialStartDateFromProfile(this.cropProfile, this.financialYearCalendars);

            return date;
        },
        endDate(){
            let date = ForecastingUtilService.getFinancialEndDateFromProfile(this.cropProfile, this.financialYearCalendars);

            return moment(date).add(1, 'week').format("YYYY-MM-DD");
        },
        dates(){
            let dates = [this.startDate];
            let nextDate = moment(UtilService.weeksForward(this.startDate, 1)).format("YYYY-MM-DD");

            while(nextDate < this.endDate){

                dates.push(nextDate);   
                
                let lastDate = dates[dates.length - 1];
                nextDate = moment(UtilService.weeksForward(lastDate, 1)).format("YYYY-MM-DD");
            }
            return dates;
        },  
        budgetValues(){
            if (this.cropProfile.BudgetValues) {
                return this.cropProfile.BudgetValues
                    .map(x => {
                        return {
                            date: this.getDateForBudgetValue(x),
                            value: this.formatPercentage(x.BudgetValue || 0)
                        }
                    });
            }

            return [];
        },
        chartLabels(){
            return this.timeline.map((x, i) => {
                return {
                    x: new Date(x.startDate),
                    value: `Week ${(i + 1)}`
                };
            }).filter(x => x != null).map((x) => {
                 return `WK${ForecastingUtilService.getFinancialWeek(x.x, this.financialYearCalendars)} FY${ForecastingUtilService.getFinancialYear(x.x, this.financialYearCalendars)}`;
            });
        },
        chartData(){
            let data = {
                labels: this.chartLabels,
                datasets: [
                    {
                        label: "Past Actuals",
                        borderColor: "#24F",
                        backgroundColor: "#24F",
                        lineTension: 0,
                        fill: false,
                        data: this.chartActualData,
                        hidden: this.legendOffIndexes.indexOf(0) > -1
                    },
                    {
                        label: "Future Profile",
                        borderColor: "#0F0",
                        backgroundColor: "#0F0",
                        lineTension: 0,
                        fill: false,
                        data: this.chartBudgetData,
                        hidden: this.legendOffIndexes.indexOf(1) > -1
                    }
                ]
            }

            return data;
        },
        chartTitle(){
            return `Production Curve`;
        },
        chartOptions(){
            let options = {
                maintainAspectRatio: false,
                title: {
                    display: true,
                    text: this.chartTitle
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                },
                tooltips: {
                    callbacks: {
                        label: function(tooltipItem, data) {
                            var label = data.datasets[tooltipItem.datasetIndex].label || '';

                            if (label) {
                                label += ': ';
                            }
                            label += tooltipItem.yLabel.toFixed(2);
                            return label;
                        }
                    }
                },
                scales: {
                    yAxes: [{
                        scaleLabel: {
                            labelString: this.displayValueTypeCode.toUpperCase()
                        },
                        ticks: {
                            min: 0,
                            precision: 0
                        }
                    }],
                    xAxes: [{
                        scaleLabel: {
                            labelString: 'Weeks'
                        },
                        ticks: {
                            source: "labels",
                            fontSize: 12,
                            maxRotation: 50,
                            minRotation: 50
                        }
                    }]
                }
            };

            return options;
        },
        actualValues(){
            return this.cropForecasts
                .filter(x => (x.cropLocationId || x.CropLocationID) == this.cropProfile.CropLocationID && (x.varietyId || x.VarietyID) == this.cropProfile.Variety.VarietyID)
                .map(x => {

                    return {
                        date: x.forecastDate || x.ForecastDate,
                        value: this.formatKg(x.actualValue || x.ActualValue || 0)
                    }
                });
        },
        timeline(){
            let timeline = [];
            for(let i = 0; i < this.dates.length; i++){
                let startDate = this.dates[i];
                let endDate = null;
                if(i < this.dates.length - 1){
                    endDate = UtilService.daysBack(this.dates[i + 1], 1); // The end of A is 1 day before the start of B
                } else {
                    // If this is the last date of the timeline, then we want to finish with our profile end date
                    endDate = this.endDate
                }

                startDate = startDate + "T00:00:00"
                endDate = endDate + "T00:00:00"           
             
                var actualValues = this.actualValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date));

                if(actualValues.length == 0) {
                    actualValues.push({date: startDate, value: 0});
                }
                timeline.push({
                    startDate: startDate,
                    endDate: endDate,
                    budgetValues: this.budgetValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date)),
                    actualValues: actualValues
                });
            }
            return timeline;
        },
        chartBudgetData(){
            var chartBudgetData = this.timeline.map(x => {
                let value = null
                if(x.budgetValues.length > 0){
                    value = x.budgetValues.map(y => y.value).reduce((a, b) => a + b)                    
                }

                if(UtilService.dateContainedWithin(x.startDate, x.endDate, moment())) {
                    if(x.actualValues.length > 0) {
                        value += x.actualValues.map(y => y.value).reduce((a, b) => a + b)
                    }
                }

                if(moment(x.startDate) < moment().add(-7, 'day')) {
                    value = null;
                }
                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
            return chartBudgetData;
        },
        chartActualData(){
            return this.timeline.map(x => {
                let value = null
                if(x.actualValues.length > 0){
                    value = x.actualValues.map(y => y.value).reduce((a, b) => a + b)
                }
                
                if(UtilService.dateContainedWithin(x.startDate, x.endDate, moment())) {
                    if(x.budgetValues.length > 0) {
                        value += x.budgetValues.map(y => y.value).reduce((a, b) => a + b)
                    }
                }
                 if(moment(x.startDate) >= moment()) {
                    value = null;
                }
                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
        },
    },
    methods: {
        getDateForBudgetValue(budgetValue){
            return this.dates[budgetValue.BerriesForecastProfileWeekNumber - 1];
        },
        formatPercentage(percentage){
            if(this.displayValueTypeCode.toLowerCase() == "kg"){
                return percentage * (this.cropProfile.TonnesPerHectare * this.cropProfile.CropArea * 1000);
            }

            if(this.displayValueTypeCode.toLowerCase() == "%"){
                return 100 * percentage;
            }
        },
        render(){
            if(this.$refs.cropProfileChart){
                this.$refs.cropProfileChart.render();
            }
        },
        formatKg(kg){
            if(this.displayValueTypeCode.toLowerCase() == "kg"){
                return kg;
            }

            if(this.displayValueTypeCode.toLowerCase() == "%"){
                let value = (kg / (this.cropProfile.TonnesPerHectare * this.cropProfile.CropArea * 1000)) * 100;
                return Math.round(value * 1000) / 1000;
            }
        },
    },
    created() {
        this.legendOffIndexes = [];
        var _this = this;
        var original = Chart.defaults.global.legend.onClick;
        Chart.defaults.global.legend.onClick = function(e, legendItem) {
            if(_this.legendOffIndexes.indexOf(legendItem.datasetIndex) > -1) {
                _this.legendOffIndexes.splice(_this.legendOffIndexes.indexOf(legendItem.datasetIndex), 1);
            } else {
                _this.legendOffIndexes.push(legendItem.datasetIndex);
            }
            original.call(this, e, legendItem);
        };
    }
}
</script>